import { log } from '../logger';
import { v4 as uuidv4 } from 'uuid';
import * as CryptoJS from 'crypto-js';

export class GamificationBookmarkUtils {
    private static readonly SECRET_KEY = process.env['NX_PUBLIC_BOOKMARK_SECRET_KEY'] || '';

    /**
     * Calculate the segment ID based on the current timecode
     * @param timecode Current playback position in seconds
     * @param segmentSize Segment size in seconds
     * @returns Segment ID
     */
    static calculateSegmentId(timecode: number, segmentSize: number): number {
        return Math.floor(timecode / segmentSize);
    }

    /**
     * Calculate total number of segments based on video duration
     * @param duration Total video duration in seconds
     * @param segmentSize Segment size in seconds
     * @returns Total number of segments
     */
    static calculateTotalSegments(duration: number, segmentSize: number): number {
        return Math.ceil(duration / segmentSize);
    }

    /**
     * Generate a secure token for bookmark verification
     * @param epochMs Current timestamp in milliseconds
     * @param requestId Unique request ID
     * @param segmentId Current segment ID
     * @param assetId Asset ID
     * @param paId Program availability ID
     * @returns Encoded token
     */
    static generateToken(epochMs: number, requestId: string, segmentId: number, assetId: string, paId: string): string {
        // Simple token generation - in production, use proper HMAC-SHA256
        const payload = `${epochMs}:${requestId}:${segmentId}:${assetId}:${paId}`;
        // Using btoa for simple encoding - replace with proper crypto in production
        return this.hashHmacSha256(payload, this.SECRET_KEY);
    }

    /**
     * Determine if a bookmark should be sent based on segment boundary
     * @param timecode Current playback position in seconds
     * @param bookmarksInterval bookmarks interval in seconds
     * @returns True if bookmark should be sent
     */
    static shouldSendBookmark(timecode: number, bookmarksInterval: number): boolean {
        return timecode > 0 && timecode % bookmarksInterval === 0;
    }

    /**
     * Generate an array of all segment IDs from 0 to current segment
     * @param currentSegmentId Current segment ID
     * @returns Array of segment IDs
     */
    static getSegmentsArray(currentSegmentId: number): number[] {
        const segments = [];
        for (let i = 1; i <= currentSegmentId; i++) {
            segments.push(i);
        }
        return segments;
    }

    /**
     * Generate all gamification fields for a bookmark event.
     * @param timeCode Current playback position in seconds
     * @param listingId Asset/listing ID
     * @param paId Program availability ID
     * @param segmentSize Segment size in seconds
     * @returns Object containing epochMs, segments, segmentId, requestId, token
     */
    static generateGamificationFields(
        timeCode: number,
        listingId: string,
        paId: string,
        segmentSize: number
    ): {
        epochMs: number;
        segments: number[];
        requestId: string;
        token: string;
    } {
        const segmentId = this.calculateSegmentId(timeCode, segmentSize);
        const segments = this.getSegmentsArray(segmentId);
        const epochMs = Date.now();
        const requestId = uuidv4();
        const token = this.generateToken(epochMs, requestId, segmentId, listingId, paId || '');
        return { epochMs, segments: [segmentId], requestId, token };
    }

    /**
     * Generate HMAC-SHA256 hash.
     * @param data - The data to be hashed.
     * @param key - The secret key used for hashing.
     * @returns The resulting HMAC-SHA256 hash as a hexadecimal string.
     */
    private static hashHmacSha256(data: string, key: string): string {
        try {
            const hmac = CryptoJS.HmacSHA256(data, key);
            const token = CryptoJS.enc.Base64.stringify(hmac);
            return token;
        } catch (err) {
            // Handle errors gracefully, e.g., log them
            log.error('[GamificationBookmarkUtils] hashHmacSha256', err);
            return ''; // Return an empty string or handle as needed
        }
    }
}
